import { PCDBHeatingSystem } from '../components/PCDBSearch';

// Interface matching the EPC form data structure
export interface EPCFormData {
  // Report Metadata
  reportReference: string;
  assessorName: string;
  assessorNumber: string;
  assessorCompany: string;
  assessmentDate: string;
  reportLanguage: string;
  relatedPartyDisclosure: string;
  
  // Property Identification
  uprn: string;
  address: {
    line1: string;
    line2?: string;
    line3?: string;
    town: string;
    postcode: string;
  };
  
  // Transaction Details
  transactionType: string;
  tenure: string;
  inspectionDate: string;
  registrationDate: string;
  epcRecommendationCode: string;
  
  // Property Details (SAP-Data)
  builtForm: string;
  extensionsCount: number;
  habitableRoomCount: number;
  heatedRoomCount: number;
  measurementType: string;
  propertyType: string;
  
  // Energy Features
  lowEnergyFixedLighting: number;
  incandescentFixedLighting: number;
  ledFixedLighting: number;
  cflFixedLighting: number;
  
  // Ventilation
  mechanicalVentilation: string;
  wetRoomsCount: number;
  extractFansCount: number;
  psvCount: number;
  
  // Air Permeability
  pressureTest: string;
  airPermeability?: number;
  pressureTestCertificate?: string;
  hasDraughtLobby: boolean;
  
  // Chimneys and Flues
  openChimneysCount: number;
  blockedChimneysCount: number;
  openFluesCount: number;
  closedFluesCount: number;
  boilerFluesCount: number;
  otherFluesCount: number;
  fluelessGasFiresCount: number;
  
  // Main Heating System (PCDB integrated)
  mainHeatingSystem: PCDBHeatingSystem | null;
  heatingSystemIndexNumber?: number;
  
  // Secondary Heating
  secondaryHeatingType: string;
  secondaryHeatingFuel: string;
  
  // Hot Water
  hotWaterType: string;
  hotWaterFuel: string;
  hotWaterEfficiency: number;
  solarWaterHeating: boolean;
  
  // Building Fabric
  walls: Array<{
    type: string;
    area: number;
    uValue: number;
    description: string;
  }>;
  
  floors: Array<{
    type: string;
    area: number;
    uValue: number;
    description: string;
  }>;
  
  roofs: Array<{
    type: string;
    area: number;
    uValue: number;
    description: string;
  }>;
  
  windows: Array<{
    type: string;
    area: number;
    uValue: number;
    orientation: string;
    description: string;
  }>;
  
  // Renewable Energy
  renewableEnergySources: Array<{
    type: string;
    capacity: number;
    efficiency: number;
  }>;
}

// SAP Rating calculation result interface
export interface SAPRating {
  currentRating: number;
  potentialRating: number;
  currentEI: number;
  potentialEI: number;
  energyConsumptionCurrent: number;
  energyConsumptionPotential: number;
  co2EmissionsCurrent: number;
  co2EmissionsPotential: number;
  heatingCostCurrent: number;
  heatingCostPotential: number;
  hotWaterCostCurrent: number;
  hotWaterCostPotential: number;
  lightingCostCurrent: number;
  lightingCostPotential: number;
  totalFloorArea: number;
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validates EPC form data for completeness and compliance
 */
export function validateEPCData(data: EPCFormData): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required fields validation
  if (!data.assessorName) errors.push('Assessor name is required');
  if (!data.assessorNumber) errors.push('Assessor registration number is required');
  if (!data.address.line1) errors.push('Property address is required');
  if (!data.address.postcode) errors.push('Postcode is required');
  if (!data.mainHeatingSystem) errors.push('Main heating system must be selected from PCDB');
  
  // DEA number format validation
  if (data.assessorNumber && !/^[A-Z]{4}\d{6}$/.test(data.assessorNumber)) {
    warnings.push('DEA number format may be incorrect (expected format: STRO123456)');
  }
  
  // Postcode format validation
  if (data.address.postcode && !/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(data.address.postcode)) {
    warnings.push('Postcode format may be incorrect');
  }
  
  // Logical validation
  if (data.heatedRoomCount > data.habitableRoomCount) {
    errors.push('Heated room count cannot exceed habitable room count');
  }
  
  // PCDB system validation
  if (data.mainHeatingSystem) {
    if (!data.mainHeatingSystem.sap_winter_seasonal_efficiency) {
      warnings.push('Selected heating system missing efficiency data');
    }
    if (!data.mainHeatingSystem.pcdb_reference) {
      warnings.push('Selected heating system missing PCDB reference');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Simplified SAP calculation for demonstration
 * In production, this would integrate with official SAP calculation software
 */
export async function calculateSAPRating(data: EPCFormData): Promise<SAPRating> {
  // This is a simplified calculation for demonstration
  // Real SAP calculations require complex algorithms and official software
  
  const baseRating = 50; // Starting baseline
  let currentRating = baseRating;
  let potentialRating = baseRating;
  
  // Heating system efficiency impact
  if (data.mainHeatingSystem?.sap_winter_seasonal_efficiency) {
    const efficiency = data.mainHeatingSystem.sap_winter_seasonal_efficiency;
    const efficiencyBonus = Math.floor((efficiency - 70) / 2);
    currentRating += efficiencyBonus;
    potentialRating += efficiencyBonus + 10; // Assume improvements
  }
  
  // Property type impact
  if (data.propertyType === '1') { // Flat
    currentRating += 5; // Generally more efficient
    potentialRating += 5;
  }
  
  // Lighting efficiency
  const totalLights = data.lowEnergyFixedLighting + data.incandescentFixedLighting;
  if (totalLights > 0) {
    const lowEnergyRatio = data.lowEnergyFixedLighting / totalLights;
    currentRating += Math.floor(lowEnergyRatio * 10);
    potentialRating += 10; // Assume all lights upgraded
  }
  
  // Extensions penalty
  currentRating -= data.extensionsCount * 2;
  potentialRating -= data.extensionsCount * 2;
  
  // Ensure ratings are within valid range (1-100)
  currentRating = Math.max(1, Math.min(100, currentRating));
  potentialRating = Math.max(currentRating, Math.min(100, potentialRating));
  
  // Environmental Impact (EI) ratings - typically similar to SAP
  const currentEI = Math.max(1, Math.min(100, currentRating - 2));
  const potentialEI = Math.max(currentEI, Math.min(100, potentialRating - 2));
  
  // Estimate floor area (simplified)
  const estimatedFloorArea = data.habitableRoomCount * 15; // 15m² per room average
  
  // Calculate energy consumption and costs (simplified formulas)
  const energyConsumptionCurrent = Math.max(50, 400 - (currentRating * 3));
  const energyConsumptionPotential = Math.max(50, 400 - (potentialRating * 3));
  
  const co2EmissionsCurrent = energyConsumptionCurrent * 0.25;
  const co2EmissionsPotential = energyConsumptionPotential * 0.25;
  
  const heatingCostCurrent = Math.floor(energyConsumptionCurrent * 0.8);
  const heatingCostPotential = Math.floor(energyConsumptionPotential * 0.8);
  
  const hotWaterCostCurrent = Math.floor(energyConsumptionCurrent * 0.15);
  const hotWaterCostPotential = Math.floor(energyConsumptionPotential * 0.15);
  
  const lightingCostCurrent = Math.floor(energyConsumptionCurrent * 0.05);
  const lightingCostPotential = Math.floor(energyConsumptionPotential * 0.05);

  return {
    currentRating,
    potentialRating,
    currentEI,
    potentialEI,
    energyConsumptionCurrent,
    energyConsumptionPotential,
    co2EmissionsCurrent,
    co2EmissionsPotential,
    heatingCostCurrent,
    heatingCostPotential,
    hotWaterCostCurrent,
    hotWaterCostPotential,
    lightingCostCurrent,
    lightingCostPotential,
    totalFloorArea: estimatedFloorArea
  };
}

/**
 * Generates RdSAP-compliant XML for official EPC submission
 */
export function generateEPCXML(data: EPCFormData, sapRating: SAPRating): string {
  const now = new Date().toISOString();
  const reportRef = data.reportReference || generateReportReference();
  
  // Generate heating system index from PCDB data
  const heatingSystemIndex = data.mainHeatingSystem?.boiler_id ? 
    parseInt(data.mainHeatingSystem.boiler_id.replace(/\D/g, '')) || 1 : 1;
  
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<RdSAP-Report xmlns="https://epbr.digital.communities.gov.uk/xsd/rdsap" 
              xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
              xsi:schemaLocation="https://epbr.digital.communities.gov.uk/xsd/rdsap RdSAP-Report.xsd">
  
  <!-- Software Information -->
  <Calculation-Software-Name>NOOR Floor Plan EPC Generator</Calculation-Software-Name>
  <Calculation-Software-Version>1.0.0</Calculation-Software-Version>
  <User-Interface-Name>NOOR Floor Plan App</User-Interface-Name>
  <User-Interface-Version>1.0.0</User-Interface-Version>
  <Schema-Version-Original>21.0.1</Schema-Version-Original>
  <Schema-Version-Current>21.0.1</Schema-Version-Current>
  
  <!-- Report Header -->
  <Report-Header>
    <RRN>${reportRef}</RRN>
    <Inspection-Date>${data.inspectionDate}</Inspection-Date>
    <Report-Type>2</Report-Type>
    <Completion-Date>${data.assessmentDate}</Completion-Date>
    <Registration-Date>${data.registrationDate}</Registration-Date>
    <Status>entered</Status>
    <Language-Code>${data.reportLanguage}</Language-Code>
    <Tenure>${data.tenure}</Tenure>
    <Transaction-Type>${data.transactionType}</Transaction-Type>
    <EPC-Recommendation-Code>${data.epcRecommendationCode}</EPC-Recommendation-Code>
    <Related-Party-Disclosure>${data.relatedPartyDisclosure}</Related-Party-Disclosure>
    
    <!-- Property Details -->
    <Property>
      <Property-Address>
        <Address-Line-1>${escapeXml(data.address.line1)}</Address-Line-1>
        ${data.address.line2 ? `<Address-Line-2>${escapeXml(data.address.line2)}</Address-Line-2>` : ''}
        ${data.address.line3 ? `<Address-Line-3>${escapeXml(data.address.line3)}</Address-Line-3>` : ''}
        <Post-Town>${escapeXml(data.address.town)}</Post-Town>
        <Postcode>${data.address.postcode}</Postcode>
      </Property-Address>
      ${data.uprn ? `<UPRN>${data.uprn}</UPRN>` : ''}
    </Property>
    
    <!-- Energy Assessor -->
    <Energy-Assessor>
      <Certificate-Number>${data.assessorNumber}</Certificate-Number>
      <Name>${escapeXml(data.assessorName)}</Name>
      ${data.assessorCompany ? `<Accreditation-Scheme-Name>${escapeXml(data.assessorCompany)}</Accreditation-Scheme-Name>` : ''}
    </Energy-Assessor>
  </Report-Header>
  
  <!-- Calculated-Assessment-Result -->
  <Calculated-Assessment-Result>
    <!-- Energy Assessment -->
    <Energy-Assessment>
      <!-- Property Summary -->
      <Property-Summary>
        <Wall>
          <Description>Solid brick, as built, no insulation (assumed)</Description>
          <Energy-Efficiency-Rating>1</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>1</Environmental-Efficiency-Rating>
        </Wall>
        <Roof>
          <Description>Pitched, 150 mm loft insulation</Description>
          <Energy-Efficiency-Rating>3</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>3</Environmental-Efficiency-Rating>
        </Roof>
        <Floor>
          <Description>Solid, no insulation (assumed)</Description>
          <Energy-Efficiency-Rating>1</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>1</Environmental-Efficiency-Rating>
        </Floor>
        <Window>
          <Description>Fully double glazed</Description>
          <Energy-Efficiency-Rating>3</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>3</Environmental-Efficiency-Rating>
        </Window>
        <Main-Heating>
          <Description>${generateHeatingDescription(data.mainHeatingSystem)}</Description>
          <Energy-Efficiency-Rating>${getEfficiencyRating(data.mainHeatingSystem?.sap_winter_seasonal_efficiency || 70)}</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>${getEfficiencyRating(data.mainHeatingSystem?.sap_winter_seasonal_efficiency || 70)}</Environmental-Efficiency-Rating>
        </Main-Heating>
        <Main-Heating-Controls>
          <Description>Programmer, room thermostat and TRVs</Description>
          <Energy-Efficiency-Rating>4</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>4</Environmental-Efficiency-Rating>
        </Main-Heating-Controls>
        <Hot-Water>
          <Description>From main system</Description>
          <Energy-Efficiency-Rating>3</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>3</Environmental-Efficiency-Rating>
        </Hot-Water>
        <Lighting>
          <Description>${generateLightingDescription(data)}</Description>
          <Energy-Efficiency-Rating>${getLightingEfficiencyRating(data)}</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>${getLightingEfficiencyRating(data)}</Environmental-Efficiency-Rating>
        </Lighting>
        <Secondary-Heating>
          <Description>None</Description>
          <Energy-Efficiency-Rating>0</Energy-Efficiency-Rating>
          <Environmental-Efficiency-Rating>0</Environmental-Efficiency-Rating>
        </Secondary-Heating>
        <Has-Hot-Water-Cylinder>true</Has-Hot-Water-Cylinder>
        <Has-Heated-Separate-Conservatory>false</Has-Heated-Separate-Conservatory>
        <Dwelling-Type>${getDwellingTypeDescription(data)}</Dwelling-Type>
        <Total-Floor-Area>${sapRating.totalFloorArea}</Total-Floor-Area>
        <Has-Fixed-Air-Conditioning>false</Has-Fixed-Air-Conditioning>
        <Multiple-Glazed-Proportion>100</Multiple-Glazed-Proportion>
      </Property-Summary>
      
      <!-- Energy Use -->
      <Energy-Use>
        <Energy-Rating-Current>${sapRating.currentRating}</Energy-Rating-Current>
        <Energy-Rating-Potential>${sapRating.potentialRating}</Energy-Rating-Potential>
        <Energy-Rating-Average>60</Energy-Rating-Average>
        <Environmental-Impact-Current>${sapRating.currentEI}</Environmental-Impact-Current>
        <Environmental-Impact-Potential>${sapRating.potentialEI}</Environmental-Impact-Potential>
        <Energy-Consumption-Current>${sapRating.energyConsumptionCurrent}</Energy-Consumption-Current>
        <Energy-Consumption-Potential>${sapRating.energyConsumptionPotential}</Energy-Consumption-Potential>
        <CO2-Emissions-Current>${sapRating.co2EmissionsCurrent.toFixed(1)}</CO2-Emissions-Current>
        <CO2-Emissions-Current-Per-Floor-Area>${(sapRating.co2EmissionsCurrent * 1000 / sapRating.totalFloorArea).toFixed(0)}</CO2-Emissions-Current-Per-Floor-Area>
        <CO2-Emissions-Potential>${sapRating.co2EmissionsPotential.toFixed(1)}</CO2-Emissions-Potential>
        <Lighting-Cost-Current>${sapRating.lightingCostCurrent}</Lighting-Cost-Current>
        <Lighting-Cost-Potential>${sapRating.lightingCostPotential}</Lighting-Cost-Potential>
        <Heating-Cost-Current>${sapRating.heatingCostCurrent}</Heating-Cost-Current>
        <Heating-Cost-Potential>${sapRating.heatingCostPotential}</Heating-Cost-Potential>
        <Hot-Water-Cost-Current>${sapRating.hotWaterCostCurrent}</Hot-Water-Cost-Current>
        <Hot-Water-Cost-Potential>${sapRating.hotWaterCostPotential}</Hot-Water-Cost-Potential>
      </Energy-Use>
      
      <!-- Suggested Improvements -->
      <Suggested-Improvements>
        <Improvement>
          <Sequence>1</Sequence>
          <Improvement-Category>1</Improvement-Category>
          <Improvement-Type>B</Improvement-Type>
          <Typical-Saving>200</Typical-Saving>
          <Energy-Performance-Rating>${Math.min(100, sapRating.currentRating + 5)}</Energy-Performance-Rating>
          <Environmental-Impact-Rating>${Math.min(100, sapRating.currentEI + 5)}</Environmental-Impact-Rating>
          <Improvement-Details>
            <Improvement-Number>1</Improvement-Number>
          </Improvement-Details>
          <Indicative-Cost>£2,500 - £8,000</Indicative-Cost>
        </Improvement>
      </Suggested-Improvements>
      
      <!-- Renewable Heat Incentive -->
      <Renewable-Heat-Incentive>
        <RHI-New-Dwelling-Baseline>39</RHI-New-Dwelling-Baseline>
        <RHI-New-Dwelling-Fabric-Energy-Efficiency>92</RHI-New-Dwelling-Fabric-Energy-Efficiency>
      </Renewable-Heat-Incentive>
    </Energy-Assessment>
  </Calculated-Assessment-Result>
  
  <!-- SAP Data (Collected Data) -->
  <SAP-Data>
    <SAP-Property-Details>
      <Built-Form>${data.builtForm}</Built-Form>
      <Extensions-Count>${data.extensionsCount}</Extensions-Count>
      <Habitable-Room-Count>${data.habitableRoomCount}</Habitable-Room-Count>
      <Heated-Room-Count>${data.heatedRoomCount}</Heated-Room-Count>
      ${data.lowEnergyFixedLighting > 0 ? `<Low-Energy-Fixed-Lighting-Bulbs-Count>${data.lowEnergyFixedLighting}</Low-Energy-Fixed-Lighting-Bulbs-Count>` : ''}
      <Incandescent-Fixed-Lighting-Bulbs-Count>${data.incandescentFixedLighting}</Incandescent-Fixed-Lighting-Bulbs-Count>
      ${data.ledFixedLighting > 0 ? `<LED-Fixed-Lighting-Bulbs-Count>${data.ledFixedLighting}</LED-Fixed-Lighting-Bulbs-Count>` : ''}
      ${data.cflFixedLighting > 0 ? `<CFL-Fixed-Lighting-Bulbs-Count>${data.cflFixedLighting}</CFL-Fixed-Lighting-Bulbs-Count>` : ''}
      <Measurement-Type>${data.measurementType}</Measurement-Type>
      <Property-Type>${data.propertyType}</Property-Type>
      <Solar-Water-Heating>${data.solarWaterHeating}</Solar-Water-Heating>
      ${data.wetRoomsCount > 0 ? `<Wet-Rooms-Count>${data.wetRoomsCount}</Wet-Rooms-Count>` : ''}
      <Pressure-Test>${data.pressureTest}</Pressure-Test>
      ${data.pressureTestCertificate ? `<Pressure-Test-Certificate-Number>${escapeXml(data.pressureTestCertificate)}</Pressure-Test-Certificate-Number>` : ''}
      ${data.airPermeability ? `<Air-Permeability>${data.airPermeability}</Air-Permeability>` : ''}
      ${data.pressureTest === '3' ? `<Has-Draught-Lobby>${data.hasDraughtLobby}</Has-Draught-Lobby>` : ''}
      ${data.openChimneysCount > 0 ? `<Open-Chimneys-Count>${data.openChimneysCount}</Open-Chimneys-Count>` : ''}
      ${data.blockedChimneysCount > 0 ? `<Blocked-Chimneys-Count>${data.blockedChimneysCount}</Blocked-Chimneys-Count>` : ''}
      ${data.openFluesCount > 0 ? `<Open-Flues-Count>${data.openFluesCount}</Open-Flues-Count>` : ''}
      ${data.closedFluesCount > 0 ? `<Closed-Flues-Count>${data.closedFluesCount}</Closed-Flues-Count>` : ''}
      ${data.boilerFluesCount > 0 ? `<Boilers-Flues-Count>${data.boilerFluesCount}</Boilers-Flues-Count>` : ''}
      ${data.otherFluesCount > 0 ? `<Other-Flues-Count>${data.otherFluesCount}</Other-Flues-Count>` : ''}
      ${data.extractFansCount > 0 ? `<Extract-Fans-Count>${data.extractFansCount}</Extract-Fans-Count>` : ''}
      ${data.psvCount > 0 ? `<PSV-Count>${data.psvCount}</PSV-Count>` : ''}
      ${data.fluelessGasFiresCount > 0 ? `<Flueless-Gas-Fires-Count>${data.fluelessGasFiresCount}</Flueless-Gas-Fires-Count>` : ''}
      ${data.mainHeatingSystem?.boiler_id ? `<Main-Heating-Index-Number>${heatingSystemIndex}</Main-Heating-Index-Number>` : ''}
      <Mechanical-Ventilation>${data.mechanicalVentilation}</Mechanical-Ventilation>
    </SAP-Property-Details>
  </SAP-Data>
  
</RdSAP-Report>`;

  return xml;
}

// Helper functions

function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function generateReportReference(): string {
  // Generate a valid RRN format: 9999-9999-9999-9999-9999
  const segments = [];
  for (let i = 0; i < 5; i++) {
    segments.push(Math.floor(Math.random() * 9000 + 1000).toString());
  }
  return segments.join('-');
}

function generateHeatingDescription(heatingSystem: PCDBHeatingSystem | null): string {
  if (!heatingSystem) return 'Boiler and radiators, unknown efficiency';
  
  const brand = heatingSystem.brand || 'Unknown';
  const model = heatingSystem.model || '';
  const efficiency = heatingSystem.sap_winter_seasonal_efficiency || 70;
  const fuel = heatingSystem.fuel || 'gas';
  const type = heatingSystem.type || 'boiler';
  
  return `${brand} ${model} ${type}, ${fuel} ${efficiency}% efficiency`.trim();
}

function generateLightingDescription(data: EPCFormData): string {
  const total = data.lowEnergyFixedLighting + data.incandescentFixedLighting + data.ledFixedLighting + data.cflFixedLighting;
  if (total === 0) return 'No fixed lighting';
  
  const lowEnergyTotal = data.lowEnergyFixedLighting + data.ledFixedLighting + data.cflFixedLighting;
  const percentage = Math.round((lowEnergyTotal / total) * 100);
  
  if (percentage >= 90) return 'Low energy lighting';
  if (percentage >= 50) return 'Some low energy lighting';
  return 'Mainly incandescent lighting';
}

function getDwellingTypeDescription(data: EPCFormData): string {
  const builtForm = data.builtForm;
  const propertyType = data.propertyType;
  
  if (propertyType === '1') return 'Flat';
  if (propertyType === '2') return 'Maisonette';
  
  // House types
  switch (builtForm) {
    case '1': return 'Detached house';
    case '2': return 'Semi-detached house';
    case '3': return 'End-terrace house';
    case '4': return 'Mid-terrace house';
    case '5': return 'Enclosed end-terrace house';
    case '6': return 'Enclosed mid-terrace house';
    default: return 'House';
  }
}

function getEfficiencyRating(efficiency: number): number {
  if (efficiency >= 94) return 5; // Very Good
  if (efficiency >= 86) return 4; // Good
  if (efficiency >= 78) return 3; // Average
  if (efficiency >= 70) return 2; // Poor
  return 1; // Very Poor
}

function getLightingEfficiencyRating(data: EPCFormData): number {
  const total = data.lowEnergyFixedLighting + data.incandescentFixedLighting + data.ledFixedLighting + data.cflFixedLighting;
  if (total === 0) return 3; // Average if no data
  
  const lowEnergyTotal = data.lowEnergyFixedLighting + data.ledFixedLighting + data.cflFixedLighting;
  const percentage = (lowEnergyTotal / total) * 100;
  
  if (percentage >= 90) return 5; // Very Good
  if (percentage >= 75) return 4; // Good
  if (percentage >= 50) return 3; // Average
  if (percentage >= 25) return 2; // Poor
  return 1; // Very Poor
}

 